import React, { useMemo, useState } from 'react';
import { resolveTextLocale } from '@/utils/scriptLocale';
import {
  applyWordBoundaryTransliteration,
  DRAVIDIAN_TRANSLITERATION_HINT,
  isDravidianTransliteration,
  transliterationSchemeForLanguage,
} from '@/utils/transliteration';

export interface LocalizedTextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Short language code (e.g. 'ta', 'hi', 'ar', 'ur') when the field's
   * language is already known — sets text direction/script font
   * deterministically, and (for Indic languages) enables live Latin ->
   * native-script transliteration as the user types. Omit to auto-detect
   * direction/font from the live text instead.
   */
  language?: string;
}

/**
 * Drop-in replacement for a plain single-line `<input>` — the `<input>`
 * counterpart to LocalizedTextArea, for single-line fields like
 * titles/headlines that hold translated or free-text content rather than
 * fixed Latin labels. See LocalizedTextArea's doc comment for the full
 * direction/font/transliteration behavior.
 */
const LocalizedTextInput = React.forwardRef<HTMLInputElement, LocalizedTextInputProps>(
  ({ language, value, style, type = 'text', onChange, ...rest }, ref) => {
    const stringValue = typeof value === 'string' ? value : '';
    const locale = useMemo(() => resolveTextLocale(language, stringValue), [language, stringValue]);
    const scheme = useMemo(() => transliterationSchemeForLanguage(language), [language]);
    const [transliterateEnabled, setTransliterateEnabled] = useState(true);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (scheme && transliterateEnabled) {
        const result = applyWordBoundaryTransliteration(
          stringValue,
          e.target.value,
          e.target.selectionStart ?? e.target.value.length,
          scheme
        );
        if (result) {
          e.target.value = result.value;
          e.target.setSelectionRange(result.cursor, result.cursor);
        }
      }
      onChange?.(e);
    };

    return (
      <div>
        <input
          ref={ref}
          type={type}
          value={value}
          onChange={handleChange}
          dir={locale.dir}
          lang={language || undefined}
          style={{ fontFamily: locale.fontFamily, ...style }}
          {...rest}
        />
        {scheme && (
          <div className="mt-1">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 select-none cursor-pointer">
              <input
                type="checkbox"
                className="h-3 w-3"
                checked={transliterateEnabled}
                onChange={(e) => setTransliterateEnabled(e.target.checked)}
              />
              Auto-convert typed English to {scheme.name} script
            </label>
            {transliterateEnabled && isDravidianTransliteration(language) && (
              <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{DRAVIDIAN_TRANSLITERATION_HINT}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

LocalizedTextInput.displayName = 'LocalizedTextInput';

export default LocalizedTextInput;
